<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import axios from 'axios'
import { apiBalancedProjectionsPath } from '../config/balancedProjectionsApi.js'
import { useProjectionsStore } from '../stores/projections'

const props = defineProps({
  statKey: {
    type: String,
    required: true
  },
  currentTotal: {
    type: Number,
    required: true
  },
  roster: {
    type: Array,
    required: true
  },
  teamType: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:locked'])

const projectionsStore = useProjectionsStore()
const trimValue = ref(0)
const isLocked = ref(false)

// Watch isLocked and emit changes to parent
watch(isLocked, (newValue) => {
  emit('update:locked', newValue)
}, { immediate: true })
const originalValues = ref(new Map())
const previousTotal = ref(0)
const savedTrimValue = ref(0) // Store the original trim value that was entered

// Computed to get the current trim total from projections
const currentTrimTotal = computed(() => {
  return props.roster.reduce((sum, player) => {
    if (!player || !player.player?.id) return sum
    
    const minutes = player.projected_minutes?.value || 0
    if (!minutes) return sum
    
    // Use the getPerThirtyValue function which handles base + trim logic
    const perThirtyValue = getPerThirtyValue(player.player.id, props.statKey)
    if (perThirtyValue === null) return sum
    
    const rawValue = (Number(perThirtyValue) / 30) * Number(minutes)
    return sum + (isNaN(rawValue) ? 0 : rawValue)
  }, 0)
})

// Helper to get base total (without trim adjustments)
const getBaseTotal = () => {
  return props.roster.reduce((sum, player) => {
    if (!player || !player.player?.id) return sum
    
    const projection = projectionsStore.projectionsData.fixture_projections.find(p => p.player_id === player.player.id)
    if (!projection) return sum
    
    const minutes = player.projected_minutes?.value || 0
    if (!minutes) return sum
    
    // Get base value (manual > auto > original, without trim)
    let baseValue = null
    if (projection[`${props.statKey}_manual`] != null && projection[`${props.statKey}_manual`] !== '' && projection[`${props.statKey}_manual`] > 0) {
      baseValue = projection[`${props.statKey}_manual`]
    } else if (projection[`${props.statKey}_auto`] != null && projection[`${props.statKey}_auto`] !== '' && projection[`${props.statKey}_auto`] > 0) {
      baseValue = projection[`${props.statKey}_auto`]
    } else if (projection[`${props.statKey}_original`] != null) {
      baseValue = projection[`${props.statKey}_original`]
    }
    
    if (baseValue === null) return sum
    
    const rawValue = (Number(baseValue) / 30) * Number(minutes)
    return sum + (isNaN(rawValue) ? 0 : rawValue)
  }, 0)
}

const getPerThirtyValue = (playerId, statKey) => {
  const projection = projectionsStore.projectionsData.fixture_projections.find(p => p.player_id === playerId)
  if (!projection) return null
  
  let baseValue = null

  // Priority: manual > auto > original
  if (projection[`${statKey}_manual`] != null && projection[`${statKey}_manual`] !== '' && projection[`${statKey}_manual`] > 0) {
    baseValue = projection[`${statKey}_manual`]
  } else if (projection[`${statKey}_auto`] != null && projection[`${statKey}_auto`] !== '' && projection[`${statKey}_auto`] > 0) {
    baseValue = projection[`${statKey}_auto`]
  } else if (projection[`${statKey}_original`] != null) {
    baseValue = projection[`${statKey}_original`]
  }
  
  if (baseValue === null) return null
  
  // Apply trim adjustment if it exists
  const trimValue = projection[`${statKey}_trim`] || 0
  return Number(baseValue) + Number(trimValue)
}

// Watch for changes in roster to reinitialize
watch(() => props.roster, () => {
  initializeComponent()
}, { deep: true })

// Initialize trimValue and isLocked from projections
const initializeComponent = () => {
  //  console.log('Initializing TrimControls for', props.statKey)
  
  // Calculate the base total (without any trim values)
  const baseTotal = getBaseTotal()
  previousTotal.value = Math.round(baseTotal * 100) / 100

  // Check if any player has a trim value for this stat
  const playersWithTrim = props.roster.filter(player => {
    if (!player || !player.player?.id) return false
    const projection = projectionsStore.projectionsData.fixture_projections.find(p => p.player_id === player.player.id)
    if (!projection) return false
    const trimVal = projection[`${props.statKey}_trim`]
    // console.log(`Player ${player.player?.name}: trim value for ${props.statKey}:`, trimVal)
    return trimVal != null && trimVal !== undefined && trimVal !== 0
  })
  
  // console.log(`Found ${playersWithTrim.length} players with trim values for ${props.statKey}`)
  
  if (playersWithTrim.length > 0) {
    // When locked, we want to show the original total value
    const storageKey = `trim_${props.statKey}_${projectionsStore.projectionsData?.id}`
    const storedTrimValue = localStorage.getItem(storageKey)
    
    if (storedTrimValue) {
      // Use the stored trim value
      trimValue.value = Number(storedTrimValue)
      savedTrimValue.value = Number(storedTrimValue)
    } else {
      // For existing locked data without stored trim value, 
      // we'll reset to unlocked state so user can re-enter the trim value
      isLocked.value = false
      trimValue.value = baseTotal 
      savedTrimValue.value = 0
      
      // // Clear any existing trim values since we don't know the original input
      projectionsStore.projectionsData.fixture_projections.forEach(projection => {
        // if (projection[`${props.statKey}_trim`] != null) {
        //   projection[`${props.statKey}_trim`] = 0
        // }
        if (projection[`${props.statKey}_trim`] !== null && projection[`${props.statKey}_trim`] !== undefined && projection[`${props.statKey}_trim`] !== '') {
          isLocked.value = true
        }
      })
      
      // console.log(`No stored trim value found for ${props.statKey}, resetting to unlocked state`)
      return // Exit early to avoid setting locked state
    }
    
    isLocked.value = true
    // console.log(`Setting locked state for ${props.statKey}, showing original total: ${trimValue.value}`)
    
    // Store original values for undo functionality
    playersWithTrim.forEach(player => {
      if (player && player.player?.id) {
        const projection = projectionsStore.projectionsData.fixture_projections.find(p => p.player_id === player.player.id)
        if (projection) {
          originalValues.value.set(player.player.id, {
            trim: projection[`${props.statKey}_trim`] || 0,
            original: projection[`${props.statKey}_original`]
          })
        }
      }
    })
  } else {
    // No trim values, show base total
    trimValue.value = 0
    isLocked.value = false
    // console.log(`No trim values found for ${props.statKey}, setting unlocked state with base total: ${trimValue.value}`)
  }
}

onMounted(() => {
  // initializeComponent()
})
const saveNewTrimValue = async (teamType) => {
  //  console.log("teamType local", teamType)
 projectionsStore.saveTrimValues(props.statKey, trimValue.value, teamType)
//  trimValue.value = projectionsStore.getFormattedProjectionValue(projectionsStore.projectionsData.fixture_projections.map(player => player.player_id), props.statKey)
  // console.log('trimValue', trimValue.value) 
}


const saveTrimValue = async () => {
  const baseTotal = getBaseTotal()
  const difference = trimValue.value - baseTotal
  
  // console.log('Base total:', baseTotal, 'Desired total:', trimValue.value, 'Difference:', difference)
  
  if (Math.abs(difference) < 0.01) return
  
  // Get projections that have valid data for this stat
  const validProjections = projectionsStore.projectionsData.fixture_projections.filter(projection => {
    const player = props.roster.find(p => p.player.id === projection.player_id)
    if (!player) return false
    
    // Check if projection has base value (manual > auto > original)
    const hasManual = projection[`${props.statKey}_manual`] != null && projection[`${props.statKey}_manual`] !== '' && projection[`${props.statKey}_manual`] > 0
    const hasAuto = projection[`${props.statKey}_auto`] != null && projection[`${props.statKey}_auto`] !== '' && projection[`${props.statKey}_auto`] > 0
    const hasOriginal = projection[`${props.statKey}_original`] != null && projection[`${props.statKey}_original`] > 0
    
    return hasManual || hasAuto || hasOriginal
  })
  
  // console.log('validProjections', validProjections)
  if (validProjections.length === 0) return
  
  // Store original values before making changes (only if not already locked)
  if (!isLocked.value) {
    previousTotal.value = Math.round(baseTotal * 100) / 100
    validProjections.forEach(projection => {
      originalValues.value.set(projection.player_id, {
        trim: projection[`${props.statKey}_trim`] || 0,
        original: projection[`${props.statKey}_original`]
      })
    })
  }
  
  // Calculate total base projection for proportional distribution
  const totalBaseProjection = validProjections.reduce((sum, projection) => {
    const player = props.roster.find(p => p.player.id === projection.player_id)
    if (!player) return sum
    
    // Get base value (manual > auto > original)
    let baseValue = null
    if (projection[`${props.statKey}_manual`] != null && projection[`${props.statKey}_manual`] !== '' && projection[`${props.statKey}_manual`] > 0) {
      baseValue = projection[`${props.statKey}_manual`]
    } else if (projection[`${props.statKey}_auto`] != null && projection[`${props.statKey}_auto`] !== '' && projection[`${props.statKey}_auto`] > 0) {
      baseValue = projection[`${props.statKey}_auto`]
    } else if (projection[`${props.statKey}_trim`] != null && projection[`${props.statKey}_trim`] !== '' && projection[`${props.statKey}_trim`] > 0) {
      baseValue = projection[`${props.statKey}_trim`]
    } else if (projection[`${props.statKey}_original`] != null) {
      baseValue = projection[`${props.statKey}_original`]
    }
    
    if (baseValue === null) return sum
    
    const minutes = player.projected_minutes?.value || 0
    return sum + ((Number(baseValue) / 30) * Number(minutes))
  }, 0)
  
  // Update projections using the new formula: (projection value / market total) * trim value * 30 / projected_minutes
  validProjections.forEach(projection => {
    const player = props.roster.find(p => p.player.id === projection.player_id)
    if (!player) return
    
    // Get base value (manual > auto > original)
    let baseValue = null
    if (projection[`${props.statKey}_manual`] != null && projection[`${props.statKey}_manual`] !== '' && projection[`${props.statKey}_manual`] > 0) {
      baseValue = projection[`${props.statKey}_manual`]
    } else if (projection[`${props.statKey}_auto`] != null && projection[`${props.statKey}_auto`] !== '' && projection[`${props.statKey}_auto`] > 0) {
      baseValue = projection[`${props.statKey}_auto`]
    } else if (projection[`${props.statKey}_original`] != null) {
      baseValue = projection[`${props.statKey}_original`]
    }
    
    if (baseValue === null) return
    
    const minutes = player.projected_minutes?.value || 0
    if (!minutes) return
    
    // Calculate player's projection value (per 30 converted to total)
    const playerProjectionValue = (Number(baseValue) / 30) * Number(minutes)
    
    // Apply the new formula: (projection value / market total) * trim value * 30 / projected_minutes
    const newTrimPer30 = (playerProjectionValue / totalBaseProjection) * trimValue.value * 30 / Number(minutes)
    
    //  console.log(`Player ${player.player?.name}: base=${baseValue}, projectionValue=${playerProjectionValue}, newTrimPer30=${newTrimPer30}`)
    
    // Update the projection in the store
    projection[`${props.statKey}_trim`] = newTrimPer30
  })

  try {
    await axios.post(apiBalancedProjectionsPath('projections/update'), {
      projections: projectionsStore.projectionsData.fixture_projections,
      fixture_id: projectionsStore.projectionsData.id
    })
    
    // Save the trim value to localStorage so we can show it when locked
    const storageKey = `trim_${props.statKey}_${projectionsStore.projectionsData?.id}`
    localStorage.setItem(storageKey, trimValue.value.toString())
    savedTrimValue.value = trimValue.value
    
    isLocked.value = true
  } catch (error) {
    console.error('Error saving trim values:', error)
  }
}

const undoTrim = async (teamType) => {
  projectionsStore.undoTrim(props.statKey, teamType)

}
</script>

<template>
  <div class="flex items-center gap-2 flex-col ">
    <div class="flex flex-col  items-center">
      <input 
        type="number" 
        
        v-model="trimValue" 
        class="w-[60px] border-1 border-gray-800 rounded-md px-1"
        :class="{'text-transparent': isLocked}"
        :disabled="isLocked"
      />
      <!-- Debug info -->
      <div class="text-xs text-gray-400 mt-1">
        {{ isLocked ? 'Locked' : 'Unlocked' }}
      </div>
    </div>
    <button 
      v-if="!isLocked"
      @click="saveNewTrimValue(teamType)" 
      class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm transition-colors"
    >
      Save
    </button>
    <button 
      v-if="isLocked"
      @click="undoTrim(teamType)" 
      class="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-sm transition-colors"
    >
      Undo
    </button>
  </div>
</template> 